import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";

/**
 * Daily EOD entry — GRID layout.
 *   Group 1  Aggregator sales (Petpooja bifurcation: Swiggy, Zomato, Toing, Magicpin, …)
 *   Group 2  Mode of payment received (Cash, UPI, Card, Dues, …)
 *   Group 3  Variable expenses (Groceries, Central Kitchen, Hira Enterprises, Dairy, …)
 *   Group 4  Fixed expenses (Rent, Salaries, Electricity, …)
 *
 * Each row in a grid has its own inline input + Save button so a manager can
 * key one line at a time without a batch commit — matches how they actually work.
 * Admin can add new channels/categories inline at the bottom of each grid.
 */
export default function DailyEntry() {
  const today = new Date().toISOString().slice(0, 10);
  const [me, setMe] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [cats, setCats] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [date, setDate] = useState(today);
  const [entries, setEntries] = useState({ sales: [], expenses: [], sales_total: 0, expense_total: 0 });
  const isAdmin = me?.role === "admin";
  const myOutletName = outlets.find(o => o.id === (isAdmin ? outletId : me?.outlet_id))?.name;

  useEffect(() => {
    Promise.all([
      api.get("/auth/me").then(r => { setMe(r.data); if (r.data.role !== "admin") setOutletId(r.data.outlet_id || ""); }).catch(() => toast.error("Session expired")),
      api.get("/ops/outlets").then(r => setOutlets(r.data)).catch(() => toast.error("Could not load outlets")),
      api.get("/ops/channels").then(r => setChannels(r.data)).catch(() => toast.error("Could not load channels")),
      api.get("/ops/expense-cats").then(r => setCats(r.data)).catch(() => toast.error("Could not load categories")),
    ]);
  }, []);

  const reload = async () => {
    const params = new URLSearchParams();
    if (isAdmin && outletId) params.set("outlet_id", outletId);
    if (date) params.set("date", date);
    try { const r = await api.get(`/ops/entries?${params}`); setEntries(r.data); } catch { /* silent */ }
  };
  useEffect(() => { if (isAdmin ? outletId : me) reload(); }, [outletId, date, me]);

  const net = entries.sales_total - entries.expense_total;

  return (
    <div className="space-y-5" data-testid="daily-entry-root">
      <div className="rounded-2xl p-5 border" style={{ background: "#FFFDF6", borderColor: "#E4D6A8" }}>
        <div className="flex flex-wrap gap-3 items-end">
          {isAdmin ? (
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
              <select data-testid="entry-outlet" value={outletId} onChange={ev => setOutletId(ev.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
                <option value="">— pick outlet —</option>
                {outlets.map(o => <option key={o.id} value={o.id}>{o.name}{o.outlet_code ? ` · ID ${o.outlet_code}` : ""}</option>)}
              </select>
            </div>
          ) : (
            <div data-testid="entry-outlet-fixed">
              <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
              <div className="h-9 px-3 flex items-center rounded border bg-[#FFF8E7] font-medium" style={{borderColor:"#E4D6A8", color:"#2b1a0d"}}>
                {myOutletName || "—"}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Date</label>
            <input data-testid="entry-date" type="date" value={date} onChange={ev => setDate(ev.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
          </div>
          <div className="ml-auto grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Sales today</div><b data-testid="sales-today-total">₹{entries.sales_total.toLocaleString()}</b></div>
            <div><div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Expenses today</div><b data-testid="exp-today-total">₹{entries.expense_total.toLocaleString()}</b></div>
            <div><div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Net today</div><b data-testid="net-today-total" style={{color: net>=0?"#0a7a3b":"#c0392b"}}>₹{net.toLocaleString()}</b></div>
          </div>
        </div>
      </div>

      {/* SALES: aggregator + payment grids */}
      <SalesGrid
        title="Aggregator sales · Petpooja bifurcation"
        subtitle="Sales that came through delivery apps and third-party platforms"
        rows={channels.filter(c => c.channel_type === "aggregator")}
        entries={entries.sales}
        outletId={outletId} date={date} me={me}
        onSaved={reload}
        isAdmin={isAdmin}
        onAddChannel={(name) => addChannel(name, "aggregator")}
        addTestId="add-aggr-channel"
        emptyHint="No aggregator channels yet — ask admin to add Swiggy / Zomato / Toing / Magicpin."
      />

      <SalesGrid
        title="Mode of payment received · in-store"
        subtitle="Sales taken at the counter, split by the mode the customer used to pay"
        rows={channels.filter(c => c.channel_type === "payment")}
        entries={entries.sales}
        outletId={outletId} date={date} me={me}
        onSaved={reload}
        isAdmin={isAdmin}
        onAddChannel={(name) => addChannel(name, "payment")}
        addTestId="add-pay-channel"
        emptyHint="No payment modes yet — ask admin to add Cash / UPI / Card / Dues."
      />

      {/* EXPENSES grids */}
      <ExpenseGrid
        title="Variable expenses · day-to-day"
        subtitle="Everything that changes with the day's business volume"
        rows={cats.filter(c => c.kind === "Variable")}
        entries={entries.expenses}
        outletId={outletId} date={date} me={me}
        onSaved={reload}
        isAdmin={isAdmin}
        onAddCat={(name) => addCat(name, "Variable")}
        addTestId="add-var-cat"
      />

      <ExpenseGrid
        title="Fixed expenses · monthly"
        subtitle="Recurring monthly outflows (rent, salaries, utilities, subscriptions)"
        rows={cats.filter(c => c.kind === "Fixed")}
        entries={entries.expenses}
        outletId={outletId} date={date} me={me}
        onSaved={reload}
        isAdmin={isAdmin}
        onAddCat={(name) => addCat(name, "Fixed")}
        addTestId="add-fix-cat"
      />
    </div>
  );

  async function addChannel(name, type) {
    try {
      await api.post("/ops/channels", { name, channel_type: type });
      toast.success(`Channel "${name}" added`);
      const r = await api.get("/ops/channels"); setChannels(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function addCat(name, kind) {
    try {
      await api.post("/ops/expense-cats", { name, kind });
      toast.success(`Category "${name}" added`);
      const r = await api.get("/ops/expense-cats"); setCats(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
}

// -----------------------------------------------------------------------------
function SalesGrid({ title, subtitle, rows, entries, outletId, date, me, onSaved, isAdmin, onAddChannel, addTestId, emptyHint }) {
  const isAdminMode = me?.role === "admin";
  const canSubmit = isAdminMode ? !!outletId : !!me;
  const totalsByName = useMemo(() => {
    const t = {};
    for (const r of entries) t[r.channel] = (t[r.channel] || 0) + r.amount;
    return t;
  }, [entries]);
  const countsByName = useMemo(() => {
    const c = {}; for (const r of entries) c[r.channel] = (c[r.channel] || 0) + 1; return c;
  }, [entries]);
  const [draft, setDraft] = useState({}); // { [channelName]: {amount, bills, notes} }
  const [busy, setBusy] = useState({});
  const [newName, setNewName] = useState("");
  const setField = (name, k, v) => setDraft(d => ({ ...d, [name]: { ...(d[name] || {}), [k]: v } }));

  const save = async (channelName) => {
    const d = draft[channelName] || {};
    const amt = parseFloat(d.amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount > 0");
    if (!canSubmit) return toast.error("Pick an outlet first");
    setBusy(b => ({ ...b, [channelName]: true }));
    try {
      await api.post("/ops/sales", {
        outlet_id: outletId || null, date,
        channel: channelName, amount: amt,
        bills: parseInt(d.bills || 0), notes: d.notes || "",
      });
      toast.success(`+₹${amt.toLocaleString()} · ${channelName}`);
      setDraft(dd => ({ ...dd, [channelName]: {} }));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(b => ({ ...b, [channelName]: false })); }
  };

  const rowTotal = rows.reduce((s, r) => s + (totalsByName[r.name] || 0), 0);

  return (
    <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4D6A8" }} data-testid={`grid-${addTestId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h3 className="font-display text-lg" style={{color:"#2b1a0d"}}>{title}</h3>
          <div className="text-xs" style={{color:"#7a6349"}}>{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Subtotal</div>
          <b style={{color:"#2b1a0d"}}>₹{rowTotal.toLocaleString()}</b>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm p-3 rounded" style={{ background: "#FFFDF6", color: "#7a6349" }}>{emptyHint}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
                <th className="text-left py-2">Channel</th>
                <th className="text-right py-2 w-32">Amount ₹</th>
                <th className="text-right py-2 w-20">Bills</th>
                <th className="text-left py-2 w-40">Notes</th>
                <th className="text-right py-2 w-32">Today so far</th>
                <th className="py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const d = draft[row.name] || {};
                const b = !!busy[row.name];
                return (
                  <tr key={row.id} className="border-t" style={{ borderColor: "#F3E7C4" }}>
                    <td className="py-2 pr-3 font-medium" style={{color:"#2b1a0d"}}>{row.name}</td>
                    <td className="py-2 pr-2">
                      <input data-testid={`amt-${row.name.toLowerCase().replace(/\s+/g,'-')}`} type="number" value={d.amount || ""} onChange={e => setField(row.name, "amount", e.target.value)}
                        placeholder="0" className="w-full h-8 px-2 text-right rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" value={d.bills || ""} onChange={e => setField(row.name, "bills", e.target.value)}
                        placeholder="0" className="w-full h-8 px-2 text-right rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                    </td>
                    <td className="py-2 pr-2">
                      <input value={d.notes || ""} onChange={e => setField(row.name, "notes", e.target.value)}
                        placeholder="optional" className="w-full h-8 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                    </td>
                    <td className="py-2 pr-2 text-right">
                      {totalsByName[row.name] ? (
                        <span data-testid={`sum-${row.name.toLowerCase().replace(/\s+/g,'-')}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background:"#F5EFE0", color:"#3B2519" }}>
                          <Check size={12} /> ₹{totalsByName[row.name].toLocaleString()} <span className="opacity-60">×{countsByName[row.name]}</span>
                        </span>
                      ) : <span className="text-xs" style={{color:"#b0967a"}}>—</span>}
                    </td>
                    <td className="py-2 pl-2">
                      <Button data-testid={`save-${row.name.toLowerCase().replace(/\s+/g,'-')}`} size="sm" onClick={() => save(row.name)} disabled={b}
                        className="w-full h-8 rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] disabled:opacity-60 text-xs">
                        {b ? "…" : "Save"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isAdminMode && (
        <div className="mt-3 flex items-center gap-2 pt-3 border-t" style={{ borderColor: "#F3E7C4" }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New channel name (e.g. Zepto, DotPe)"
            className="h-8 px-2 rounded border bg-white text-sm flex-1" style={{borderColor:"#E4D6A8"}}
            data-testid={`${addTestId}-name`} />
          <Button size="sm" data-testid={addTestId} onClick={() => { if(newName.trim()){ onAddChannel(newName.trim()); setNewName(""); }}}
            className="h-8 rounded-full bg-[#3B2519] hover:bg-[#5a3826] text-[#F5C242] text-xs">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
      )}
      {/* Today's entries list with delete */}
      {rows.some(r => totalsByName[r.name]) && (
        <TodayRows kind="sales" items={entries.filter(r => rows.some(x => x.name === r.channel))} onDeleted={onSaved} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function ExpenseGrid({ title, subtitle, rows, entries, outletId, date, me, onSaved, isAdmin, onAddCat, addTestId }) {
  const isAdminMode = me?.role === "admin";
  const canSubmit = isAdminMode ? !!outletId : !!me;
  const totalsByName = useMemo(() => {
    const t = {}; for (const r of entries) t[r.category] = (t[r.category] || 0) + r.amount; return t;
  }, [entries]);
  const countsByName = useMemo(() => {
    const c = {}; for (const r of entries) c[r.category] = (c[r.category] || 0) + 1; return c;
  }, [entries]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState({});
  const [newName, setNewName] = useState("");
  const setField = (name, k, v) => setDraft(d => ({ ...d, [name]: { ...(d[name] || {}), [k]: v } }));

  const save = async (catName) => {
    const d = draft[catName] || {};
    const amt = parseFloat(d.amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount > 0");
    if (!canSubmit) return toast.error("Pick an outlet first");
    setBusy(b => ({ ...b, [catName]: true }));
    try {
      await api.post("/ops/expenses", {
        outlet_id: outletId || null, date,
        category: catName, amount: amt,
        vendor: d.vendor || "", notes: d.notes || "",
      });
      toast.success(`-₹${amt.toLocaleString()} · ${catName}`);
      setDraft(dd => ({ ...dd, [catName]: {} }));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(b => ({ ...b, [catName]: false })); }
  };

  const rowTotal = rows.reduce((s, r) => s + (totalsByName[r.name] || 0), 0);

  return (
    <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4D6A8" }} data-testid={`grid-${addTestId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h3 className="font-display text-lg" style={{color:"#2b1a0d"}}>{title}</h3>
          <div className="text-xs" style={{color:"#7a6349"}}>{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Subtotal</div>
          <b style={{color:"#c0392b"}}>₹{rowTotal.toLocaleString()}</b>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm p-3 rounded" style={{ background: "#FFFDF6", color: "#7a6349" }}>No categories yet — ask admin to add some.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
                <th className="text-left py-2">Category</th>
                <th className="text-right py-2 w-32">Amount ₹</th>
                <th className="text-left py-2 w-40">Vendor</th>
                <th className="text-left py-2 w-40">Notes</th>
                <th className="text-right py-2 w-32">Today so far</th>
                <th className="py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const d = draft[row.name] || {};
                const b = !!busy[row.name];
                const safeId = row.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                return (
                  <tr key={row.id} className="border-t" style={{ borderColor: "#F3E7C4" }}>
                    <td className="py-2 pr-3 font-medium" style={{color:"#2b1a0d"}}>{row.name}</td>
                    <td className="py-2 pr-2">
                      <input data-testid={`amt-${safeId}`} type="number" value={d.amount || ""} onChange={e => setField(row.name, "amount", e.target.value)}
                        placeholder="0" className="w-full h-8 px-2 text-right rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                    </td>
                    <td className="py-2 pr-2">
                      <input value={d.vendor || ""} onChange={e => setField(row.name, "vendor", e.target.value)}
                        placeholder="Vendor" className="w-full h-8 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                    </td>
                    <td className="py-2 pr-2">
                      <input value={d.notes || ""} onChange={e => setField(row.name, "notes", e.target.value)}
                        placeholder="Notes" className="w-full h-8 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                    </td>
                    <td className="py-2 pr-2 text-right">
                      {totalsByName[row.name] ? (
                        <span data-testid={`sum-${safeId}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background:"#F5EFE0", color:"#3B2519" }}>
                          <Check size={12} /> ₹{totalsByName[row.name].toLocaleString()} <span className="opacity-60">×{countsByName[row.name]}</span>
                        </span>
                      ) : <span className="text-xs" style={{color:"#b0967a"}}>—</span>}
                    </td>
                    <td className="py-2 pl-2">
                      <Button data-testid={`save-${safeId}`} size="sm" onClick={() => save(row.name)} disabled={b}
                        className="w-full h-8 rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] disabled:opacity-60 text-xs">
                        {b ? "…" : "Save"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isAdminMode && (
        <div className="mt-3 flex items-center gap-2 pt-3 border-t" style={{ borderColor: "#F3E7C4" }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New expense category name"
            className="h-8 px-2 rounded border bg-white text-sm flex-1" style={{borderColor:"#E4D6A8"}}
            data-testid={`${addTestId}-name`} />
          <Button size="sm" data-testid={addTestId} onClick={() => { if(newName.trim()){ onAddCat(newName.trim()); setNewName(""); }}}
            className="h-8 rounded-full bg-[#3B2519] hover:bg-[#5a3826] text-[#F5C242] text-xs">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
      )}
      {rows.some(r => totalsByName[r.name]) && (
        <TodayRows kind="expenses" items={entries.filter(r => rows.some(x => x.name === r.category))} onDeleted={onSaved} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function TodayRows({ kind, items, onDeleted }) {
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/ops/${kind}/${id}`); toast.success("Deleted"); onDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!items.length) return null;
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "#F3E7C4" }}>
      <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{ color: "#7a6349" }}>Entries logged today ({items.length})</div>
      <div className="flex flex-wrap gap-2">
        {items.map(r => (
          <span key={r.id} className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs border" style={{ borderColor:"#E4D6A8", background:"#FFF8E7", color:"#3B2519" }}>
            {kind === "sales" ? r.channel : r.category} · <b>₹{r.amount.toLocaleString()}</b>
            {r.bills ? ` · ${r.bills} bills` : ""}
            {r.vendor ? ` · ${r.vendor}` : ""}
            {r.notes ? ` · ${r.notes}` : ""}
            <button data-testid={`del-${kind}-${r.id}`} onClick={() => del(r.id)} className="ml-1 text-red-500 hover:text-red-700"><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}
